import { CircleAlert, CircleCheck, LoaderCircle } from 'lucide-react';
import type { ReactNode } from 'react';

import { productName, productTagline } from '../../app/brand';
import { Logo } from '../../components/Logo';

/** The frame of the pages used without a session: sign in, password reset. */
export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <div className="grid min-h-dvh place-items-center bg-canvas px-4 py-10">
      <main className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3.5">
          <Logo className="size-12" />
          <div className="leading-tight">
            <div className="text-2xl font-semibold tracking-tight text-fg">{productName}</div>
            <div className="mt-0.5 text-[13px] text-fg-subtle">{productTagline}</div>
          </div>
        </div>
        <div className="rounded-lg border border-line bg-surface p-6 shadow-xl shadow-black/20">
          <h1 className="text-base font-semibold text-fg">{title}</h1>
          <p className="mt-1 text-[13px] text-fg-muted">{subtitle}</p>
          {children}
        </div>
        <p className="mt-6 text-center text-2xs text-fg-subtle">
          Acesso restrito. As tentativas de acesso são registradas.
        </p>
      </main>
    </div>
  );
}

export const authInputClass =
  'h-9 w-full rounded-md border border-line-strong bg-canvas px-3 text-[13px] text-fg placeholder:text-fg-subtle transition-colors hover:border-fg-subtle/60 focus:border-accent focus-visible:outline-offset-0';

export function AuthField({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-medium text-fg-muted">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-2xs text-fg-subtle">{hint}</p>}
    </div>
  );
}

/** A form-level message: an error, or a confirmation. */
export function AuthNotice({
  id,
  tone,
  children,
}: {
  id?: string;
  tone: 'error' | 'success';
  children: ReactNode;
}) {
  const error = tone === 'error';
  const Icon = error ? CircleAlert : CircleCheck;
  return (
    <div
      id={id}
      role={error ? 'alert' : 'status'}
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-[13px] ${
        error ? 'border-crit/30 bg-crit-soft text-crit' : 'border-ok/30 bg-ok-soft text-ok'
      }`}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0" strokeWidth={2} />
      <div>{children}</div>
    </div>
  );
}

export function AuthSubmit({
  busy,
  disabled,
  label,
  busyLabel,
}: {
  busy: boolean;
  disabled: boolean;
  label: string;
  busyLabel: string;
}) {
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-accent-strong text-[13px] font-medium text-white transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
    >
      {busy && <LoaderCircle aria-hidden="true" className="size-4 animate-spin" strokeWidth={2} />}
      {busy ? busyLabel : label}
    </button>
  );
}
